import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  EntityTable,
  EditorMode,
  EntityActionType,
  EntityField,
  useTypeSelectors,
  EntityAction,
  Button,
  Tabs as libTabs,
  TitleActionContainer,
  base,
} from '@sdxcorp/sdx-dds-gui-lib';
import { useNavigation, adminRouteTo } from '../../../../routing';
import { AdminAuthContext } from '../../../../auth/authContext';
import { useAuth } from '../../../../../base/auth/useAuth';
import { customInstructionCellRenderers, customInstructionsHeaderCellRenderers } from '../../../../extensions';
import { SettlementInstructionField, SettlementInstructionMatchingStatus, SettlementInstructionTypeSelector } from '../../../../constants';
import { fromPairs } from 'lodash';
import { AdminAbilities } from '../../../../auth/abilities';
import { computeSubTypeFilter } from './settlementInstruction_utils';

const {
  organisms: { LayoutContent },
  atoms: {
    Heading,
    Icons: { Add, Holdings, Edit, SuccessIcon },
  },
} = base;

const { Tabs, Tab, TabTitle } = libTabs;

export const TYPE = 'SettlementInstruction';

export enum TabName {
  ALLEGEMENTS = 'allegements',
  OPEN_INSTRUCTIONS = 'open_instructions',
  CLOSED_INSTRUCTIONS = 'closed_instructions',
  ALL_INSTRUCTIONS = 'all_instructions',
}

const tabNames: string[] = [TabName.ALLEGEMENTS, TabName.OPEN_INSTRUCTIONS, TabName.CLOSED_INSTRUCTIONS, TabName.ALL_INSTRUCTIONS];
const colsWidthWeight = {
  creationDateTime: 1.2,
  typeSelector: 0.8,
  processingStatusAdditionalInformation: 2,
  settlementQuantity: 0.8,
  settlementAmount: 0.8,
  'settlementAmountCurrency.code': 0.8,
  'counterParty.aliasName': 1,
  'instructingParty.aliasName': 1,
};
const getTabIndex = (name: string) => {
  const index = tabNames.indexOf(name);
  return index === -1 ? 0 : index;
};

export const MATCHING_SUBTYPES: Record<string, string> = fromPairs([
  [SettlementInstructionTypeSelector.DVP, SettlementInstructionTypeSelector.RVP],
  [SettlementInstructionTypeSelector.RVP, SettlementInstructionTypeSelector.DVP],
  [SettlementInstructionTypeSelector.RFP, SettlementInstructionTypeSelector.DFP],
  [SettlementInstructionTypeSelector.DFP, SettlementInstructionTypeSelector.RFP],
  [SettlementInstructionTypeSelector.PMO, SettlementInstructionTypeSelector.RMO],
  [SettlementInstructionTypeSelector.RMO, SettlementInstructionTypeSelector.PMO],
  [SettlementInstructionTypeSelector.CA_DFP, SettlementInstructionTypeSelector.CA_RFP],
  [SettlementInstructionTypeSelector.CA_RFP, SettlementInstructionTypeSelector.CA_DFP],
  [SettlementInstructionTypeSelector.CA_PMO, SettlementInstructionTypeSelector.CA_RMO],
  [SettlementInstructionTypeSelector.CA_RMO, SettlementInstructionTypeSelector.CA_PMO],
]);

const TYPE_SELECTOR_KEY = '__typeSelector';

const ORDER_BY = { fieldName: 'creationDateTime', direction: 'DESC' } as const;

export function setsIntersection<T>(xs: T[], ys: T[]): T[] {
  const xss = new Set<T>(xs);
  const yss = new Set<T>(ys);
  return [...yss].filter(y => xss.has(y));
}

export const allTypeSelectorsBut = (xs: SettlementInstructionTypeSelector[]): SettlementInstructionTypeSelector[] =>
  Object.values(SettlementInstructionTypeSelector).filter(ts => !xs.includes(ts));

const onExchangeTypeSelectors = [SettlementInstructionTypeSelector.ON_EXCHANGE_DVP, SettlementInstructionTypeSelector.ON_EXCHANGE_RVP];
const xcsdTypeSelectors = [SettlementInstructionTypeSelector.CROSS_CSD_DFP, SettlementInstructionTypeSelector.CROSS_CSD_RFP];
const atTypeSelector = [SettlementInstructionTypeSelector.AT];
const issDeissTypeSelectors = [SettlementInstructionTypeSelector.ISSUANCE, SettlementInstructionTypeSelector.DEISSUANCE];
const cryptoTypeSelectors = [
  SettlementInstructionTypeSelector.CC_DEPOSIT,
  SettlementInstructionTypeSelector.CC_WITHDRAWAL,
  SettlementInstructionTypeSelector.CC_INTRA_WITHDRAWAL,
];
const assetSbTrfTypeSelector = [SettlementInstructionTypeSelector.ASSET_SUBBALANCE_TRANSFER];

const allTypeSelectorsButCrypto = allTypeSelectorsBut(cryptoTypeSelectors);
const allTypeSelectorsButAtIssDeissAssetSbTrf = allTypeSelectorsBut(issDeissTypeSelectors.concat(atTypeSelector, assetSbTrfTypeSelector));
const allTypeSelectorsButIssDeissAssetSbTrfCrypto = allTypeSelectorsBut(issDeissTypeSelectors.concat(cryptoTypeSelectors, assetSbTrfTypeSelector));
const allTypeSelectorsButXcsdIssDeissAssetSbTrfCrypto = allTypeSelectorsBut(
  xcsdTypeSelectors.concat(issDeissTypeSelectors, cryptoTypeSelectors, assetSbTrfTypeSelector),
);

const ednUuid = (x: string): string => `#uuid \\"${x}\\"`;

export function ednSet<T>(xs: T[]): string {
  return '#{' + xs.map(ts => '\\"' + ts + '\\"').join(' ') + '}';
}

const buildAllInstructionsQuery = (memberId: string) =>
  '(or-join [?e]' +
  `  (and [?e :instructingParty ${ednUuid(memberId)}]` +
  `    [?e :typeSelector ?p0][(contains? ${ednSet(allTypeSelectorsButIssDeissAssetSbTrfCrypto)} ?p0)])` +
  `  (and [?e :instructingParty ${ednUuid(memberId)}]` +
  `    [?e :typeSelector ?p1][(contains? ${ednSet(issDeissTypeSelectors)} ?p1)]` +
  `    [?e :asset ?isNat][?isNat :isNativeAsset true])` +
  `  (and [?e :typeSelector ?p2][(contains? ${ednSet(setsIntersection(onExchangeTypeSelectors, allTypeSelectorsButCrypto))} ?p2)])` +
  `  (and [?e :transactingParty ${ednUuid(memberId)}]` +
  `    [?e :typeSelector ?p3][(contains? ${ednSet(setsIntersection(xcsdTypeSelectors, allTypeSelectorsButCrypto))} ?p3)])` +
  `  (and [?e :safekeepingAccount ?sa] [?sa :accountRef]` +
  `    [?e :typeSelector ?p4][(contains? ${ednSet(allTypeSelectorsButIssDeissAssetSbTrfCrypto)} ?p4)])` +
  `  (and [?e :safekeepingAccount ?sa] [?sa :accountRef]` +
  `    [?e :typeSelector ?p5][(contains? ${ednSet(issDeissTypeSelectors)} ?p5)]` +
  `    [?e :asset ?isNat][?isNat :isNativeAsset true])` +
  ')';

const buildAllInstructionsQueryForTechnicalIssuer = (memberId: string) =>
  '(or-join [?e]' +
  `  (and [?e :instructingParty ${ednUuid(memberId)}]` +
  `    [?e :typeSelector ?p6][(contains? ${ednSet(allTypeSelectorsButXcsdIssDeissAssetSbTrfCrypto)} ?p6)])` +
  `  (and [?e :instructingParty ${ednUuid(memberId)}]` +
  `    [?e :typeSelector ?p7][(contains? ${ednSet(issDeissTypeSelectors)} ?p7)]` +
  `    [?e :asset ?isNat][?isNat :isNativeAsset true])` +
  `  (and [?e :typeSelector ?p8]` +
  `    [(contains? ${ednSet(setsIntersection(onExchangeTypeSelectors, allTypeSelectorsButCrypto))} ?p8)])` +
  `  (and [?e :safekeepingAccount ?sa] [?sa :accountRef]` +
  `    [?e :typeSelector ?p9][(contains? ${ednSet(allTypeSelectorsButXcsdIssDeissAssetSbTrfCrypto)} ?p9)])` +
  `  (and [?e :safekeepingAccount ?sa] [?sa :accountRef]` +
  `    [?e :typeSelector ?p10][(contains? ${ednSet(issDeissTypeSelectors)} ?p10)]` +
  `    [?e :asset ?isNat][?isNat :isNativeAsset true])` +
  ')';

const buildOpenInstructionsQuery = (memberId: string) =>
  '[?e :processingStatusCode ?p11][(contains? #{:RECEIVED :PENDING :ACCEPTED} ?p11)]' + buildAllInstructionsQuery(memberId);

const buildOpenInstructionsQueryForTechnicalIssuer = (memberId: string) =>
  `[?e :processingStatusCode ?p12][(contains? #{:RECEIVED :PENDING :ACCEPTED} ?p12)]` + buildAllInstructionsQueryForTechnicalIssuer(memberId);

const buildClosedInstructionsQuery = (memberId: string) =>
  '[?e :processingStatusCode ?p13][(contains? #{:REJECTED :SETTLED :CANCELLED} ?p13)]' + buildAllInstructionsQuery(memberId);

const buildClosedInstructionsQueryForTechnicalIssuer = (memberId: string) =>
  '[?e :processingStatusCode ?p14][(contains? #{:REJECTED :SETTLED :CANCELLED} ?p14)]' + buildAllInstructionsQueryForTechnicalIssuer(memberId);

const buildAllegementsQuery = (memberId: string) =>
  '[?e :processingStatusCode ?p15][(contains? #{:RECEIVED :PENDING :ACCEPTED} ?p15)]' +
  `[?e :counterParty ${ednUuid(memberId)}]` +
  `[?e :typeSelector ${ednSet(setsIntersection(allTypeSelectorsButAtIssDeissAssetSbTrf, allTypeSelectorsButCrypto))}]` +
  '[?e :matchingStatusCode #{:UNMATCHED nil}]';

export const createCounterInstruction = (push: (route: string) => void, t: TFunction, tab: TabName, typeSelector?: string): EntityAction => ({
  type: EntityActionType.CLIENT,
  label: t('transactions.settlementInstructions.action.counterInstruction'),
  action: 'CounterInstruction',
  buttonConfig: {
    eventHandler: data => {
      const matchingSubType = MATCHING_SUBTYPES[((typeSelector || data[TYPE_SELECTOR_KEY]) as string)?.toLowerCase()];
      if (matchingSubType) {
        push(adminRouteTo.settlementInstructionFrom({ subType: matchingSubType, id: data[EntityField.ID] as string, tab }));
      }
    },
  },
  selectorFields: [SettlementInstructionField.MATCHING_STATUS_CODE],
  selector: data =>
    MATCHING_SUBTYPES[((typeSelector || data[TYPE_SELECTOR_KEY]) as string)?.toLowerCase()] !== undefined &&
    data[SettlementInstructionField.MATCHING_STATUS_CODE] !== SettlementInstructionMatchingStatus.MATCHED,
});

export const getSIOverviewCount = (TYPE: string, memberId: string, isTechnicalIssuerAgent: boolean) => [
  {
    entityType: TYPE,
    fieldMap: {},
    filterQuery: buildAllegementsQuery(memberId),
    filterArgs: [],
    resultLimit: 6,
  },
  {
    entityType: TYPE,
    fieldMap: {},
    filterQuery: isTechnicalIssuerAgent ? buildOpenInstructionsQueryForTechnicalIssuer(memberId) : buildOpenInstructionsQuery(memberId),
    filterArgs: [],
    resultLimit: 6,
  },
  {
    entityType: TYPE,
    fieldMap: {},
    filterQuery: isTechnicalIssuerAgent ? buildClosedInstructionsQueryForTechnicalIssuer(memberId) : buildClosedInstructionsQuery(memberId),
    filterArgs: [],
    resultLimit: 6,
  },
];

export const itemsCountToBadge = (count: number) => {
  if (count > 5) {
    return '5+';
  }
  return count;
};

const Instructions = () => {
  const { member } = useAuth(AdminAuthContext);
  const { t } = useTranslation();
  const [operations, setOperations] = useState<
    | {
        create: boolean;
        read: boolean;
        update: boolean;
        delete: boolean;
      }
    | undefined
  >();

  const { canI } = useAuth<AdminAbilities>(AdminAuthContext);

  const isCryptoEnv = canI('asset-types-cryptocurrency');
  const isTechnicalIssuerAgent = canI('technical-issuer-agent');

  const {
    push,
    params: { tab },
    query,
  } = useNavigation();

  const title = t('transactions.settlementInstructions.overview.title');
  const buttonTitle = t('transactions.settlementInstructions.overview.create');
  const subTypeFilter = useMemo(() => computeSubTypeFilter(isCryptoEnv), [isCryptoEnv]);
  const selectors = useTypeSelectors({ type: TYPE, isOverview: true }).filter(s => subTypeFilter(s.type));

  useEffect(() => {
    if (tab === undefined) {
      push(adminRouteTo.settlementInstructions({ filter: query }));
    }
  }, [tab, query, push]);

  const onTabChange = (index: number) => {
    push(adminRouteTo.settlementInstructions({ tab: tabNames[index], filter: query }));
  };

  const onCreateClick = () => {
    const firstSelector = selectors.find(s => s.rank === 1);
    if (firstSelector) {
      push(adminRouteTo.settlementInstructionDetails({ mode: EditorMode.CREATE, subType: firstSelector.type }));
    } else {
      push(adminRouteTo.settlementInstructionDetails({ mode: EditorMode.CREATE }));
    }
  };

  const onRowClick = (tab: TabName) => (id: string, type: string, subType?: string) => {
    push(adminRouteTo.settlementInstructionDetails({ mode: EditorMode.VIEW, subType, id, tab }));
  };

  return (
    <LayoutContent fullWidth>
      <TitleActionContainer
        marginBottom="20px"
        titleElement={<Heading level="1">{title}</Heading>}
        sideElement={
          operations?.create ? (
            <Button
              variant="primary"
              testId="create-entity"
              icon={<Add />}
              onClick={onCreateClick}
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              {buttonTitle}
            </Button>
          ) : null
        }
      />
      <Tabs disableMaxWidth onActive={onTabChange} activeIndex={getTabIndex(tab)}>
        <Tab
          testId="tab-item-allegements"
          variant="transparent"
          title={<TabTitle title={t('transactions.settlementInstructions.overview.tabs.allegements')} icon={<Holdings />} />}
        >
          {member && (
            <EntityTable
              key="allegements-table"
              view={isCryptoEnv ? 'settlement-instructions-crypto' : 'settlement-instructions'}
              entityType={TYPE}
              colsWidthWeight={colsWidthWeight}
              onRowClick={onRowClick(TabName.ALLEGEMENTS)}
              clientActions={[createCounterInstruction(push, t, TabName.ALLEGEMENTS)]}
              // BE does additional filtering: transactionTypeCode != CORP && typeSelector != Account Transfer, Issuance, Deissuance
              filterQuery={buildAllegementsQuery(member.id as string)}
              onOperations={setOperations}
              orderBy={ORDER_BY}
              customCellRenderers={customInstructionCellRenderers(['settlementQuantity', 'settlementAmount'])}
              customHeaderCellRenderers={customInstructionsHeaderCellRenderers()}
              filterHiddenFields={['counterParty.aliasName']}
            />
          )}
        </Tab>
        <Tab
          testId="tab-item-open_instructions"
          variant="transparent"
          title={<TabTitle title={t('transactions.settlementInstructions.overview.tabs.open_instructions')} icon={<Edit />} />}
        >
          {member && (
            <EntityTable
              key="open-instructions-table"
              view={isCryptoEnv ? 'settlement-instructions-crypto' : 'settlement-instructions'}
              entityType={TYPE}
              colsWidthWeight={colsWidthWeight}
              onRowClick={onRowClick(TabName.OPEN_INSTRUCTIONS)}
              clientActions={[createCounterInstruction(push, t, TabName.OPEN_INSTRUCTIONS)]}
              filterQuery={
                isTechnicalIssuerAgent
                  ? buildOpenInstructionsQueryForTechnicalIssuer(member.id as string)
                  : buildOpenInstructionsQuery(member.id as string)
              }
              onOperations={setOperations}
              orderBy={ORDER_BY}
              customCellRenderers={customInstructionCellRenderers()}
              customHeaderCellRenderers={customInstructionsHeaderCellRenderers()}
            />
          )}
        </Tab>
        <Tab
          testId="tab-item-closed_instructions"
          variant="transparent"
          title={<TabTitle title={t('transactions.settlementInstructions.overview.tabs.closed_instructions')} icon={<SuccessIcon />} />}
        >
          {member && (
            <EntityTable
              key="closed-instructions-table"
              view={isCryptoEnv ? 'settlement-instructions-crypto' : 'settlement-instructions'}
              entityType={TYPE}
              colsWidthWeight={colsWidthWeight}
              onRowClick={onRowClick(TabName.CLOSED_INSTRUCTIONS)}
              filterQuery={
                isTechnicalIssuerAgent
                  ? buildClosedInstructionsQueryForTechnicalIssuer(member.id as string)
                  : buildClosedInstructionsQuery(member.id as string)
              }
              onOperations={setOperations}
              orderBy={ORDER_BY}
              customCellRenderers={customInstructionCellRenderers()}
              customHeaderCellRenderers={customInstructionsHeaderCellRenderers()}
            />
          )}
        </Tab>
        <Tab
          testId="tab-item-all_instructions"
          variant="transparent"
          floatRight
          title={<TabTitle title={t('transactions.settlementInstructions.overview.tabs.all_instructions')} />}
        >
          {member && (
            <EntityTable
              key="all-instructions-table"
              view={isCryptoEnv ? 'settlement-instructions-crypto' : 'settlement-instructions'}
              entityType={TYPE}
              colsWidthWeight={colsWidthWeight}
              onRowClick={onRowClick(TabName.ALL_INSTRUCTIONS)}
              filterQuery={
                isTechnicalIssuerAgent
                  ? buildAllInstructionsQueryForTechnicalIssuer(member.id as string)
                  : buildAllInstructionsQuery(member.id as string)
              }
              onOperations={setOperations}
              orderBy={ORDER_BY}
              customCellRenderers={customInstructionCellRenderers()}
              customHeaderCellRenderers={customInstructionsHeaderCellRenderers()}
            />
          )}
        </Tab>
      </Tabs>
    </LayoutContent>
  );
};

export default Instructions;
